/**
 * CAD ribbon + classic menu bar definitions (Faz 3 — OpenCADStudio parity).
 *
 * Data-only model (no JSX): the ribbon shell (components/layout/ribbon/*)
 * is a thin renderer over these tables, mirroring the OCS plugin RibbonItem
 * model (docs/opencadstudio-parity/03-ribbon-gui-shell.md §2.1).
 *
 * Dead-button contract: every item's `cmdId` is typed as
 * `RegisteredCommandId` — the literal union derived from the canonical
 * command table — so a ribbon button can only reference a command that is
 * actually registered (the static counterpart of OCS's
 * every_menu_entry_runs_a_known_command check). `shell` bindings are the
 * application-level actions (document save, clipboard) that are not CAD
 * commands, and `tool` bindings activate a viewport tool directly.
 */

import {
    AlignLeft,
    AlignVerticalJustifyCenter,
    ArrowDownWideNarrow,
    ArrowUpRight,
    Ban,
    Baseline,
    Blocks,
    Box,
    Brush,
    Clipboard,
    ClipboardCopy,
    ClipboardPaste,
    Compass,
    Component,
    Copy,
    CopyPlus,
    Circle,
    CircleDot,
    CircleEllipsis,
    Construction,
    Database,
    Disc,
    Eraser,
    Equal,
    Eye,
    EyeOff,
    FileSpreadsheet,
    FileStack,
    FileText,
    FlipHorizontal2,
    Frame,
    Grid2x2,
    Grid3x3,
    Group,
    Hand,
    HelpCircle,
    Image,
    Import,
    Infinity as InfinityIcon,
    LayoutTemplate,
    Layers,
    Link2,
    List,
    ListChecks,
    LocateFixed,
    Lock,
    Magnet,
    Maximize,
    Minus,
    MousePointer2,
    Move,
    MoveVertical,
    Package,
    PanelLeft,
    PanelTop,
    PanelTopClose,
    PenLine,
    PenTool,
    Pipette,
    Printer,
    RotateCw,
    Ruler,
    Rows2,
    Columns2,
    Save,
    Scaling,
    Scissors,
    Settings,
    Shapes,
    Sheet,
    Spline,
    Snowflake,
    Slash,
    Sparkles,
    Square,
    Stamp,
    Table,
    TableProperties,
    Tags,
    Target,
    Type,
    Unlock,
    Waves,
    Waypoints,
    Wrench,
    Zap,
    ZoomIn,
} from 'lucide-react';
import { CANONICAL_COMMANDS } from '../core/commands/canonicalCommands';

/** Closed union of every registered canonical command id. */
export type RegisteredCommandId = (typeof CANONICAL_COMMANDS)[number]['id'];

/** Icon component type (lucide) used by ribbon items. */
export type RibbonIcon = typeof Minus;

/**
 * Application-level actions the ribbon/menus drive that are NOT canonical
 * CAD commands (document lifecycle goes through the CommandRegistrar's
 * topbar ids; undo/redo through the app history store).
 */
export type ShellActionId =
    | 'doc.new' | 'doc.open' | 'doc.save' | 'doc.saveAs' | 'doc.saveAsTemplate'
    | 'edit.cut' | 'edit.copy' | 'edit.paste'
    | 'app.undo' | 'app.redo';

/** One executable binding — exactly one of the three fields is set. */
export interface RibbonBinding {
    cmdId?: RegisteredCommandId;
    shell?: ShellActionId;
    tool?: string;
}

/** One clickable entry (button face or dropdown row). */
export interface RibbonEntryDef extends RibbonBinding {
    /** Stable React key + i18n suffix (ribbon.items.<key>). */
    key: string;
    /** Optional keytip badge letter for the Alt shortcut chain (Faz 3.11). */
    keytip?: string;
    icon?: RibbonIcon;
}

export interface RibbonDropdownDef extends RibbonEntryDef {
    kind: 'dropdown' | 'largeDropdown';
    /** Rows shown in the dropdown menu (each runs its own binding). */
    items: RibbonEntryDef[];
}

export interface RibbonToolGridDef extends RibbonEntryDef {
    kind: 'toolGrid';
    /** Columns of small buttons (OCS ToolGrid). */
    columns: RibbonEntryDef[][];
}

export interface RibbonComboDef extends RibbonEntryDef {
    kind: 'layerCombo' | 'colorCombo' | 'linetypeCombo' | 'lwCombo' | 'styleCombo';
    /** StyleCombo data source (only wired stores listed; TextStyle/TableStyle land in Faz 8). */
    styleKey?: 'dimstyle' | 'mleaderStyle';
    /** Style manager command opened by the combo's trailing button. */
    managerCmd?: RegisteredCommandId;
}

export interface RibbonPanelButtonDef extends RibbonEntryDef {
    kind: 'panelButton';
    /** The Faz 5 palette ecosystem extends the Faz 3 pair. */
    panelId: 'properties' | 'layers' | 'layerManager' | 'blocks' | 'xref' | 'count' | 'quickProperties';
}

export type RibbonItem =
    | (RibbonEntryDef & { kind: 'tool' })
    | (RibbonEntryDef & { kind: 'largeTool' })
    | RibbonDropdownDef
    | RibbonToolGridDef
    | RibbonComboDef
    | RibbonPanelButtonDef;

export interface RibbonGroupDef {
    key: string;
    titleKey: string;
    /** Keytip badge for the group step of the Alt chain. */
    keytip: string;
    items: RibbonItem[];
}

export interface RibbonTabDef {
    key: string;
    titleKey: string;
    keytip: string;
    groups: RibbonGroupDef[];
    /** Contextual visibility: null = static, else a runtime predicate id. */
    contextual?: 'paperSpace' | 'blockEditor';
    /** Parametric stays hidden until the Faz 6 constraint engine lands. */
    hidden?: boolean;
}

/* ------------------------------------------------------------------ */
/* Shared entries                                                       */
/* ------------------------------------------------------------------ */

const dimensionItems: RibbonEntryDef[] = [
    { key: 'dimLinear', cmdId: 'dim.linear', icon: AlignLeft },
    { key: 'dimAligned', cmdId: 'dim.aligned', icon: AlignVerticalJustifyCenter },
    { key: 'dimAngular', cmdId: 'dim.angular', icon: Compass },
    { key: 'dimRadius', cmdId: 'dim.radius', icon: Circle },
    { key: 'dimDiameter', cmdId: 'dim.diameter', icon: CircleDot },
    { key: 'dimOrdinate', cmdId: 'dim.ordinate', icon: LocateFixed },
    { key: 'dimContinue', cmdId: 'dim.continue', icon: ArrowUpRight },
    { key: 'dimBaseline', cmdId: 'dim.baseline', icon: AlignLeft },
    { key: 'dimArcLength', cmdId: 'dim.arcLength', icon: Waypoints },
    { key: 'dimJogged', cmdId: 'dim.jogged', icon: Waypoints },
    { key: 'dimCenterMark', cmdId: 'dim.centerMark', icon: Target },
    { key: 'dimTolerance', cmdId: 'dim.tolerance', icon: Frame },
];

const blockItems: RibbonEntryDef[] = [
    { key: 'blockDefine', cmdId: 'blk.define', icon: Component, keytip: 'B' },
    { key: 'blockEdit', cmdId: 'blk.edit', icon: PenTool, keytip: 'E' },
    { key: 'blockInsert', cmdId: 'blk.insert', icon: Blocks, keytip: 'N' },
    { key: 'attdef', cmdId: 'blk.attdef', icon: Tags, keytip: 'A' },
];

/* ------------------------------------------------------------------ */
/* Tabs                                                                 */
/* ------------------------------------------------------------------ */

export const RIBBON_TABS: RibbonTabDef[] = [
    {
        key: 'home',
        titleKey: 'ribbon.tabs.home',
        keytip: 'H',
        groups: [
            {
                key: 'draw',
                titleKey: 'ribbon.groups.draw',
                keytip: 'D',
                items: [
                    { kind: 'largeTool', key: 'line', cmdId: 'draw.line', icon: PenLine, keytip: 'L' },
                    { kind: 'largeTool', key: 'polyline', cmdId: 'draw.polyline', icon: Spline, keytip: 'P' },
                    {
                        kind: 'dropdown', key: 'circle', cmdId: 'draw.circle', icon: Circle, keytip: 'C',
                        items: [
                            { key: 'circle', cmdId: 'draw.circle', icon: Circle },
                            { key: 'donut', cmdId: 'draw.donut', icon: CircleDot },
                        ],
                    },
                    {
                        kind: 'dropdown', key: 'arc', cmdId: 'draw.arc', icon: Compass, keytip: 'A',
                        items: [
                            { key: 'arc', cmdId: 'draw.arc', icon: Compass },
                            { key: 'xline', cmdId: 'draw.xline', icon: InfinityIcon },
                            { key: 'ray', cmdId: 'draw.ray', icon: ArrowUpRight },
                        ],
                    },
                    { kind: 'tool', key: 'ellipse', cmdId: 'draw.ellipse', icon: Disc, keytip: 'E' },
                    {
                        kind: 'dropdown', key: 'shapes', cmdId: 'draw.rectangle', icon: Square, keytip: 'S',
                        items: [
                            { key: 'rectangle', cmdId: 'draw.rectangle', icon: Square },
                            { key: 'polygon', cmdId: 'draw.polygon', icon: Shapes },
                            { key: 'solid2d', cmdId: 'draw.solid2d', icon: Layers },
                            { key: 'region', cmdId: 'draw.region', icon: Frame },
                            { key: 'boundary', cmdId: 'draw.boundary', icon: Box },
                            { key: 'wipeout', cmdId: 'draw.wipeout', icon: EyeOff },
                            { key: 'revisionCloud', cmdId: 'draw.revisionCloud', icon: Waypoints },
                            { key: 'gradient', cmdId: 'draw.gradient', icon: Brush },
                        ],
                    },
                    {
                        kind: 'dropdown', key: 'hatch', cmdId: 'draw.hatch', icon: Grid3x3, keytip: 'H',
                        items: [
                            { key: 'hatch', cmdId: 'draw.hatch', icon: Grid3x3 },
                            { key: 'hatchEdit', cmdId: 'draw.hatchEdit', icon: Settings },
                            { key: 'sketch', cmdId: 'draw.sketch', icon: PenTool },
                            { key: 'mline', cmdId: 'draw.mline', icon: AlignVerticalJustifyCenter },
                        ],
                    },
                ],
            },
            {
                key: 'modify',
                titleKey: 'ribbon.groups.modify',
                keytip: 'O',
                items: [
                    { kind: 'largeTool', key: 'move', cmdId: 'mod.move', icon: Move, keytip: 'M' },
                    { kind: 'largeTool', key: 'copy', cmdId: 'mod.copy', icon: Copy, keytip: 'C' },
                    { kind: 'largeTool', key: 'stretch', cmdId: 'mod.stretch', icon: Scaling, keytip: 'T' },
                    { kind: 'largeTool', key: 'rotate', cmdId: 'mod.rotate', icon: RotateCw, keytip: 'R' },
                    { kind: 'largeTool', key: 'mirror', cmdId: 'trf.mirror', icon: FlipHorizontal2, keytip: 'I' },
                    { kind: 'largeTool', key: 'scale', cmdId: 'mod.scale', icon: Scaling, keytip: 'S' },
                    {
                        kind: 'toolGrid', key: 'modifyTools', columns: [
                            [
                                { key: 'trim', cmdId: 'mod.trim', icon: Scissors, keytip: 'D' },
                                { key: 'extend', cmdId: 'mod.extend', icon: ArrowUpRight, keytip: 'X' },
                                { key: 'offset', cmdId: 'mod.offset', icon: CopyPlus, keytip: 'F' },
                                { key: 'fillet', cmdId: 'mod.fillet', icon: CircleEllipsis, keytip: 'J' },
                                { key: 'chamfer', cmdId: 'mod.chamfer', icon: Scissors },
                                { key: 'erase', cmdId: 'mod.erase', icon: Eraser, keytip: 'E' },
                            ],
                            [
                                { key: 'explode', cmdId: 'mod.explode', icon: Import, keytip: 'P' },
                                { key: 'join', cmdId: 'mod.join', icon: Group, keytip: 'N' },
                                { key: 'break', cmdId: 'mod.break', icon: Scissors },
                                { key: 'pedit', cmdId: 'mod.pedit', icon: PenLine },
                                { key: 'lengthen', cmdId: 'mod.lengthen', icon: Ruler },
                                { key: 'flatten', cmdId: 'drf.flatten', icon: ArrowDownWideNarrow },
                            ],
                        ],
                    },
                    {
                        kind: 'dropdown', key: 'array', cmdId: 'arr.general', icon: Grid3x3, keytip: 'Y',
                        items: [
                            { key: 'arrayGeneral', cmdId: 'arr.general', icon: Grid3x3 },
                            { key: 'arrayRect', cmdId: 'arr.rect', icon: Grid3x3 },
                            { key: 'arrayPolar', cmdId: 'arr.polar', icon: CircleDot },
                            { key: 'arrayPath', cmdId: 'arr.path', icon: Waypoints },
                            { key: 'arrayEdit', cmdId: 'arr.edit', icon: Settings },
                        ],
                    },
                    {
                        kind: 'dropdown', key: 'clipboardGroup', cmdId: undefined, shell: 'edit.copy', icon: Clipboard, keytip: 'K',
                        items: [
                            { key: 'copyClip', shell: 'edit.copy', icon: ClipboardCopy },
                            { key: 'cutClip', shell: 'edit.cut', icon: Scissors },
                            { key: 'pasteClip', shell: 'edit.paste', icon: ClipboardPaste },
                            { key: 'copyBase', cmdId: 'order.copyBase', icon: Copy },
                            { key: 'pasteBlock', cmdId: 'order.pasteBlock', icon: Blocks },
                            { key: 'pasteOrig', cmdId: 'order.pasteOrig', icon: LocateFixed },
                        ],
                    },
                ],
            },
            {
                key: 'annotation',
                titleKey: 'ribbon.groups.annotation',
                keytip: 'N',
                items: [
                    { kind: 'tool', key: 'text', cmdId: 'draw.text', icon: Type, keytip: 'T' },
                    {
                        kind: 'dropdown', key: 'dimensions', cmdId: 'dim.linear', icon: AlignLeft, keytip: 'D',
                        items: dimensionItems,
                    },
                    { kind: 'tool', key: 'leader', cmdId: 'ann.leader', icon: PenTool, keytip: 'L' },
                ],
            },
            {
                key: 'layers',
                titleKey: 'ribbon.groups.layers',
                keytip: 'L',
                items: [
                    { kind: 'layerCombo', key: 'layerCombo', icon: Layers },
                    {
                        kind: 'toolGrid', key: 'layerTools', columns: [
                            [
                                { key: 'layOff', cmdId: 'lay.off', icon: EyeOff, keytip: 'O' },
                                { key: 'layFreeze', cmdId: 'lay.freeze', icon: Snowflake, keytip: 'Z' },
                                { key: 'layLock', cmdId: 'lay.lock', icon: Lock, keytip: 'K' },
                                { key: 'layIso', cmdId: 'lay.iso', icon: Eye, keytip: 'I' },
                            ],
                            [
                                { key: 'layOn', cmdId: 'lay.on', icon: Eye, keytip: 'N' },
                                { key: 'layThaw', cmdId: 'lay.thaw', icon: Snowflake, keytip: 'W' },
                                { key: 'layUnlock', cmdId: 'lay.unlock', icon: Unlock, keytip: 'U' },
                                { key: 'layUniso', cmdId: 'lay.uniso', icon: EyeOff, keytip: 'S' },
                            ],
                        ],
                    },
                    { kind: 'panelButton', key: 'layerProperties', panelId: 'layers', icon: PanelLeft, keytip: 'P' },
                ],
            },
            {
                key: 'block',
                titleKey: 'ribbon.groups.block',
                keytip: 'K',
                items: [
                    { kind: 'panelButton', key: 'blocksPalette', panelId: 'blocks', icon: Blocks, keytip: 'G' },
                    ...blockItems.map((e) => ({ kind: 'tool' as const, ...e })),
                ],
            },
            {
                key: 'properties',
                titleKey: 'ribbon.groups.properties',
                keytip: 'R',
                items: [
                    { kind: 'colorCombo', key: 'colorCombo', icon: Box },
                    { kind: 'linetypeCombo', key: 'linetypeCombo', icon: Minus },
                    { kind: 'lwCombo', key: 'lwCombo', icon: Minus },
                    { kind: 'panelButton', key: 'propertiesPalette', panelId: 'properties', icon: TableProperties, keytip: 'P' },
                    { kind: 'tool', key: 'matchProp', cmdId: 'vis.matchProp', icon: Pipette, keytip: 'M' },
                ],
            },
            {
                key: 'measure',
                titleKey: 'ribbon.groups.measure',
                keytip: 'Q',
                items: [
                    { kind: 'tool', key: 'dist', cmdId: 'inq.distance', icon: Ruler, keytip: 'D' },
                    { kind: 'tool', key: 'area', cmdId: 'inq.area', icon: Square, keytip: 'A' },
                    { kind: 'tool', key: 'list', cmdId: 'inq.list', icon: List, keytip: 'L' },
                    { kind: 'tool', key: 'id', cmdId: 'inq.id', icon: LocateFixed, keytip: 'I' },
                ],
            },
        ],
    },
    {
        key: 'insert',
        titleKey: 'ribbon.tabs.insert',
        keytip: 'I',
        groups: [
            {
                key: 'block',
                titleKey: 'ribbon.groups.block',
                keytip: 'K',
                items: [
                    ...blockItems.map((e) => ({ kind: 'largeTool' as const, ...e })),
                    { kind: 'tool', key: 'attMan', cmdId: 'blk.attMan', icon: Tags },
                    { kind: 'tool', key: 'burst', cmdId: 'blk.burst', icon: Import },
                ],
            },
            {
                key: 'reference',
                titleKey: 'ribbon.groups.reference',
                keytip: 'F',
                items: [
                    { kind: 'largeTool', key: 'xrefAttach', cmdId: 'xref.attach', icon: FileStack },
                    { kind: 'largeTool', key: 'imageAttach', cmdId: 'xref.image', icon: Image },
                    { kind: 'tool', key: 'xclip', cmdId: 'xref.clip', icon: Scissors },
                    { key: 'refedit', kind: 'tool', cmdId: 'xref.edit', icon: PenTool },
                    { key: 'bind', kind: 'tool', cmdId: 'xref.bind', icon: Link2 },
                ],
            },
            {
                key: 'content',
                titleKey: 'ribbon.groups.content',
                keytip: 'T',
                items: [
                    { kind: 'largeTool', key: 'table', cmdId: 'data.table', icon: Table },
                    { kind: 'tool', key: 'dataLink', cmdId: 'data.link', icon: Database },
                    { kind: 'tool', key: 'dataExtract', cmdId: 'data.extract', icon: FileSpreadsheet },
                ],
            },
        ],
    },
    {
        key: 'annotate',
        titleKey: 'ribbon.tabs.annotate',
        keytip: 'A',
        groups: [
            {
                key: 'text',
                titleKey: 'ribbon.groups.text',
                keytip: 'T',
                items: [
                    {
                        kind: 'dropdown', key: 'mtext', cmdId: 'draw.mtext', icon: Type,
                        items: [
                            { key: 'mtext', cmdId: 'draw.mtext', icon: Type },
                            { key: 'textSingle', cmdId: 'draw.text', icon: AlignLeft },
                            { key: 'ddedit', cmdId: 'draw.ddedit', icon: PenLine },
                        ],
                    },
                    { kind: 'tool', key: 'textStyleManager', cmdId: 'draw.textStyle', icon: Baseline },
                ],
            },
            {
                key: 'dimensions',
                titleKey: 'ribbon.groups.dimensions',
                keytip: 'D',
                items: [
                    {
                        kind: 'toolGrid', key: 'dimTools', columns: [
                            dimensionItems.slice(0, 6),
                            dimensionItems.slice(6),
                        ],
                    },
                    { kind: 'styleCombo', key: 'dimStyleCombo', styleKey: 'dimstyle', managerCmd: 'dim.style', icon: TableProperties },
                    { kind: 'tool', key: 'dimSpace', cmdId: 'dim.space', icon: AlignVerticalJustifyCenter },
                    { kind: 'tool', key: 'dimBreak', cmdId: 'dim.break', icon: Scissors },
                    { kind: 'tool', key: 'dimReassociate', cmdId: 'dim.reassociate', icon: Link2 },
                ],
            },
            {
                key: 'leaders',
                titleKey: 'ribbon.groups.leaders',
                keytip: 'L',
                items: [
                    { kind: 'largeTool', key: 'mleader', cmdId: 'ann.leader', icon: PenTool },
                    { kind: 'tool', key: 'qleader', cmdId: 'ann.qleader', icon: ArrowUpRight },
                    { kind: 'styleCombo', key: 'mleaderStyleCombo', styleKey: 'mleaderStyle', managerCmd: 'ann.leaderStyle', icon: TableProperties },
                ],
            },
            {
                key: 'tables',
                titleKey: 'ribbon.groups.tables',
                keytip: 'B',
                items: [
                    { kind: 'largeTool', key: 'table', cmdId: 'data.table', icon: Table },
                ],
            },
            {
                key: 'markup',
                titleKey: 'ribbon.groups.markup',
                keytip: 'M',
                items: [
                    { kind: 'tool', key: 'wipeout', cmdId: 'draw.wipeout', icon: EyeOff },
                    { kind: 'tool', key: 'revcloud', cmdId: 'draw.revisionCloud', icon: Waypoints },
                    { kind: 'tool', key: 'revisionCloudBim', cmdId: 'ann.revisionCloud', icon: Stamp },
                ],
            },
        ],
    },
    {
        // Faz 6 (constraint engine) landed: the Parametric tab is live with
        // the geometric + dimensional constraint command set.
        key: 'parametric',
        titleKey: 'ribbon.tabs.parametric',
        keytip: 'P',
        groups: [
            {
                key: 'geometric',
                titleKey: 'ribbon.groups.geometric',
                keytip: 'G',
                items: [
                    { kind: 'largeTool', key: 'geomConstraint', cmdId: 'par.geom', icon: Magnet, keytip: 'C' },
                    {
                        kind: 'dropdown', key: 'geometricConstraints', cmdId: 'par.gcCoincident', icon: Link2, keytip: 'N',
                        items: [
                            { key: 'gcCoincident', cmdId: 'par.gcCoincident', icon: Link2 },
                            { key: 'gcHorizontal', cmdId: 'par.gcHorizontal', icon: Minus },
                            { key: 'gcVertical', cmdId: 'par.gcVertical', icon: MoveVertical },
                            { key: 'gcParallel', cmdId: 'par.gcParallel', icon: AlignVerticalJustifyCenter },
                            { key: 'gcPerpendicular', cmdId: 'par.gcPerpendicular', icon: Slash },
                            { key: 'gcTangent', cmdId: 'par.gcTangent', icon: CircleDot },
                            { key: 'gcConcentric', cmdId: 'par.gcConcentric', icon: Target },
                            { key: 'gcColinear', cmdId: 'par.gcColinear', icon: Waypoints },
                            { key: 'gcEqual', cmdId: 'par.gcEqual', icon: Equal },
                            { key: 'gcSymmetric', cmdId: 'par.gcSymmetric', icon: FlipHorizontal2 },
                            { key: 'gcMidpoint', cmdId: 'par.gcMidpoint', icon: LocateFixed },
                            { key: 'gcPointOnCurve', cmdId: 'par.gcPointOnCurve', icon: Spline },
                            { key: 'gcSmooth', cmdId: 'par.gcSmooth', icon: Waves },
                            { key: 'gcFix', cmdId: 'par.gcFix', icon: Lock },
                        ],
                    },
                    { kind: 'tool', key: 'autoconstrain', cmdId: 'par.autoconstrain', icon: Sparkles, keytip: 'A' },
                    { kind: 'tool', key: 'delConstraint', cmdId: 'par.delConstraint', icon: Eraser, keytip: 'D' },
                    {
                        kind: 'dropdown', key: 'constraintVisibility', cmdId: 'par.gcShowAll', icon: Eye, keytip: 'V',
                        items: [
                            { key: 'gcShow', cmdId: 'par.gcShow', icon: Eye },
                            { key: 'gcHide', cmdId: 'par.gcHide', icon: EyeOff },
                            { key: 'gcShowAll', cmdId: 'par.gcShowAll', icon: Eye },
                            { key: 'gcHideAll', cmdId: 'par.gcHideAll', icon: EyeOff },
                            { key: 'constraintBar', cmdId: 'par.constraintBar', icon: Settings },
                        ],
                    },
                ],
            },
            {
                key: 'dimensional',
                titleKey: 'ribbon.groups.dimensional',
                keytip: 'M',
                items: [
                    { kind: 'largeTool', key: 'dimConstraint', cmdId: 'par.dim', icon: Ruler, keytip: 'K' },
                    {
                        kind: 'dropdown', key: 'dimConstraints', cmdId: 'par.dcAligned', icon: Ruler, keytip: 'L',
                        items: [
                            { key: 'dcAligned', cmdId: 'par.dcAligned', icon: Ruler },
                            { key: 'dcHorizontal', cmdId: 'par.dcHorizontal', icon: Minus },
                            { key: 'dcVertical', cmdId: 'par.dcVertical', icon: MoveVertical },
                            { key: 'dcRadius', cmdId: 'par.dcRadius', icon: Circle },
                            { key: 'dcDiameter', cmdId: 'par.dcDiameter', icon: CircleDot },
                            { key: 'dcAngular', cmdId: 'par.dcAngular', icon: Compass },
                        ],
                    },
                    { kind: 'tool', key: 'dcForm', cmdId: 'par.dcForm', icon: Wrench, keytip: 'F' },
                ],
            },
            {
                key: 'parametricManage',
                titleKey: 'ribbon.groups.parametricManage',
                keytip: 'B',
                items: [
                    { kind: 'tool', key: 'parameters', cmdId: 'par.manager', icon: Table, keytip: 'T' },
                    { kind: 'tool', key: 'autoconstrainSettings', cmdId: 'par.autoconstrainSettings', icon: Settings, keytip: 'S' },
                ],
            },
        ],
    },
    {
        key: 'view',
        titleKey: 'ribbon.tabs.view',
        keytip: 'V',
        groups: [
            {
                key: 'navigate',
                titleKey: 'ribbon.groups.navigate',
                keytip: 'N',
                items: [
                    {
                        kind: 'dropdown', key: 'zoom', cmdId: 'view.zoom', icon: ZoomIn,
                        items: [
                            { key: 'zoom', cmdId: 'view.zoom', icon: ZoomIn },
                            { key: 'zoomExtents', cmdId: 'view.zoomExtents', icon: Maximize },
                            { key: 'zoomSelected', cmdId: 'view.zoomSelected', icon: Target },
                        ],
                    },
                    { kind: 'tool', key: 'pan', cmdId: 'view.pan', icon: Hand },
                ],
            },
            {
                key: 'interface',
                titleKey: 'ribbon.groups.interface',
                keytip: 'I',
                items: [
                    { kind: 'tool', key: 'layoutTabs', cmdId: 'view.layoutTabs', icon: PanelTop },
                    { kind: 'tool', key: 'ribbonToggle', cmdId: 'sys.ribbonToggle', icon: LayoutTemplate },
                    { kind: 'tool', key: 'menuBarToggle', cmdId: 'sys.menuBar', icon: AlignLeft },
                    // Model-space viewport tiling (Faz 4.9) + clean screen.
                    {
                        kind: 'dropdown', key: 'vports', cmdId: 'view.vportsSingle', icon: Grid2x2,
                        items: [
                            { key: 'vportsSingle', cmdId: 'view.vportsSingle', icon: Maximize },
                            { key: 'vports2h', cmdId: 'view.horizontal', icon: Rows2 },
                            { key: 'vports2v', cmdId: 'view.vertical', icon: Columns2 },
                            { key: 'vports4', cmdId: 'view.cascade', icon: Grid2x2 },
                        ],
                    },
                    { kind: 'tool', key: 'cleanScreen', cmdId: 'view.cleanScreen', icon: PanelTopClose },
                ],
            },
            {
                key: 'palettes',
                titleKey: 'ribbon.groups.palettes',
                keytip: 'P',
                items: [
                    { kind: 'panelButton', key: 'propertiesPalette', panelId: 'properties', icon: TableProperties },
                    { kind: 'panelButton', key: 'layerProperties', panelId: 'layers', icon: PanelLeft },
                    // Faz 5 palette ecosystem (G4): blocks/xref/count manager
                    // palettes + the floating Quick Properties mini palette.
                    { kind: 'panelButton', key: 'blocksPalette', panelId: 'blocks', icon: Blocks },
                    { kind: 'panelButton', key: 'xrefPalette', panelId: 'xref', icon: FileStack },
                    { kind: 'panelButton', key: 'countPalette', panelId: 'count', icon: ListChecks },
                    { kind: 'panelButton', key: 'quickPropertiesPalette', panelId: 'quickProperties', icon: Zap },
                    { kind: 'tool', key: 'toolPalettes', cmdId: 'view.toolPalette', icon: FileText },
                    { kind: 'tool', key: 'sheetSet', cmdId: 'file.sheetSet', icon: Sheet },
                ],
            },
            {
                key: 'plot',
                titleKey: 'ribbon.groups.plot',
                keytip: 'O',
                items: [
                    { kind: 'tool', key: 'pageSetup', cmdId: 'file.pageSetup', icon: Printer },
                    { kind: 'largeTool', key: 'plot', cmdId: 'file.plot', icon: Printer },
                ],
            },
        ],
    },
    {
        key: 'manage',
        titleKey: 'ribbon.tabs.manage',
        keytip: 'M',
        groups: [
            {
                key: 'customization',
                titleKey: 'ribbon.groups.customization',
                keytip: 'C',
                items: [
                    { kind: 'largeTool', key: 'cui', cmdId: 'sys.cui', icon: Wrench },
                    { kind: 'tool', key: 'aliasEdit', cmdId: 'sys.alias', icon: PenLine },
                    { kind: 'tool', key: 'appLoad', cmdId: 'sys.appLoad', icon: Package },
                ],
            },
            {
                key: 'cleanup',
                titleKey: 'ribbon.groups.cleanup',
                keytip: 'U',
                items: [
                    { kind: 'largeTool', key: 'purge', cmdId: 'file.purge', icon: Eraser },
                    { kind: 'tool', key: 'overkill', cmdId: 'file.overkill', icon: Ban },
                    { kind: 'tool', key: 'audit', cmdId: 'file.audit', icon: Construction },
                ],
            },
            {
                key: 'application',
                titleKey: 'ribbon.groups.application',
                keytip: 'A',
                items: [
                    { kind: 'tool', key: 'options', cmdId: 'sys.options', icon: Settings },
                    { kind: 'tool', key: 'help', cmdId: 'sys.help', icon: HelpCircle },
                ],
            },
        ],
    },
    {
        // Contextual: visible only while paper space is active (a layout id
        // is set). OCS Layout tab — MVIEW + plot family.
        key: 'layout',
        titleKey: 'ribbon.tabs.layout',
        keytip: 'L',
        contextual: 'paperSpace',
        groups: [
            {
                key: 'viewport',
                titleKey: 'ribbon.groups.viewport',
                keytip: 'V',
                items: [
                    { kind: 'largeTool', key: 'mview', cmdId: 'file.mview', icon: Frame },
                    { kind: 'tool', key: 'vpClip', cmdId: 'file.vpClip', icon: Scissors },
                    { kind: 'tool', key: 'vpLayer', cmdId: 'file.vpLayer', icon: Layers },
                ],
            },
            {
                key: 'plot',
                titleKey: 'ribbon.groups.plot',
                keytip: 'P',
                items: [
                    { kind: 'tool', key: 'pageSetup', cmdId: 'file.pageSetup', icon: Printer },
                    { kind: 'tool', key: 'plot', cmdId: 'file.plot', icon: Printer },
                    { kind: 'tool', key: 'publish', cmdId: 'file.publish', icon: FileSpreadsheet },
                ],
            },
        ],
    },
    {
        // Contextual: visible only while the isolated Block Editor session
        // is open (useBlockStore.editingBlock != null — the session engine
        // itself lands with Faz 5.15; BSAVE/BSAVEAS/BCLOSE/BTESTBLOCK are
        // registered-but-pending until that engine provides them).
        key: 'blockEditor',
        titleKey: 'ribbon.tabs.blockEditor',
        keytip: 'B',
        contextual: 'blockEditor',
        groups: [
            {
                key: 'openSave',
                titleKey: 'ribbon.groups.openSave',
                keytip: 'O',
                items: [
                    { kind: 'largeTool', key: 'bedit', cmdId: 'blk.edit', icon: PenTool },
                    { kind: 'tool', key: 'bsave', cmdId: 'blk.bsave', icon: Save },
                    { kind: 'tool', key: 'bsaveAs', cmdId: 'blk.bsaveAs', icon: Save },
                    { kind: 'tool', key: 'bclose', cmdId: 'blk.bclose', icon: Ban },
                    { kind: 'tool', key: 'btestBlock', cmdId: 'blk.btestBlock', icon: MousePointer2 },
                ],
            },
            {
                key: 'attributes',
                titleKey: 'ribbon.groups.attributes',
                keytip: 'A',
                items: [
                    { kind: 'largeTool', key: 'attdef', cmdId: 'blk.attdef', icon: Tags },
                ],
            },
        ],
    },
];

/* ------------------------------------------------------------------ */
/* Classic menu bar (OCS 11 menus)                                      */
/* ------------------------------------------------------------------ */

export interface MenuEntryDef extends RibbonBinding {
    key: string;
    /** Static shortcut hint shown on the right (topbar defaults). */
    shortcut?: string;
}

export interface MenuDef {
    key: string;
    entries: MenuEntryDef[];
    dividers?: number[];
}

export const MENU_BAR: MenuDef[] = [
    {
        key: 'file',
        entries: [
            { key: 'new', shell: 'doc.new', shortcut: 'Ctrl+N' },
            { key: 'open', shell: 'doc.open', shortcut: 'Ctrl+O' },
            { key: 'save', shell: 'doc.save', shortcut: 'Ctrl+S' },
            { key: 'saveAs', shell: 'doc.saveAs', shortcut: 'Ctrl+Shift+S' },
            { key: 'saveAsTemplate', shell: 'doc.saveAsTemplate' },
            { key: 'pageSetup', cmdId: 'file.pageSetup' },
            { key: 'plot', cmdId: 'file.plot' },
            { key: 'publish', cmdId: 'file.publish' },
            { key: 'audit', cmdId: 'file.audit' },
            { key: 'purge', cmdId: 'file.purge' },
        ],
        dividers: [4, 7],
    },
    {
        key: 'edit',
        entries: [
            { key: 'undo', shell: 'app.undo', shortcut: 'Ctrl+Z' },
            { key: 'redo', shell: 'app.redo', shortcut: 'Ctrl+Y' },
            { key: 'cut', shell: 'edit.cut', shortcut: 'Ctrl+X' },
            { key: 'copy', shell: 'edit.copy', shortcut: 'Ctrl+C' },
            { key: 'paste', shell: 'edit.paste', shortcut: 'Ctrl+V' },
            { key: 'copyBase', cmdId: 'order.copyBase' },
            { key: 'pasteBlock', cmdId: 'order.pasteBlock' },
            { key: 'pasteOrig', cmdId: 'order.pasteOrig' },
            { key: 'erase', cmdId: 'mod.erase' },
        ],
        dividers: [1, 4],
    },
    {
        key: 'view',
        entries: [
            { key: 'regen', cmdId: 'view.regen' },
            { key: 'zoom', cmdId: 'view.zoom' },
            { key: 'pan', cmdId: 'view.pan' },
            { key: 'grid', cmdId: 'view.grid' },
            { key: 'ortho', cmdId: 'view.ortho' },
            { key: 'polar', cmdId: 'view.polar' },
            { key: 'isodraft', cmdId: 'view.isodraft' },
            { key: 'osnap', cmdId: 'view.osnap' },
            { key: 'tilemode', cmdId: 'view.tilemode' },
            { key: 'layoutTabs', cmdId: 'view.layoutTabs' },
            { key: 'ribbonToggle', cmdId: 'sys.ribbonToggle' },
            { key: 'menuBarToggle', cmdId: 'sys.menuBar' },
            { key: 'toolPalettes', cmdId: 'view.toolPalette' },
        ],
        dividers: [0, 2, 8],
    },
    {
        key: 'insert',
        entries: [
            { key: 'block', cmdId: 'blk.insert' },
            { key: 'blockDefine', cmdId: 'blk.define' },
            { key: 'xref', cmdId: 'xref.attach' },
            { key: 'image', cmdId: 'xref.image' },
            { key: 'xclip', cmdId: 'xref.clip' },
            { key: 'refedit', cmdId: 'xref.edit' },
            { key: 'bind', cmdId: 'xref.bind' },
            { key: 'table', cmdId: 'data.table' },
            { key: 'dataLink', cmdId: 'data.link' },
            { key: 'dataExtract', cmdId: 'data.extract' },
        ],
        dividers: [1, 6],
    },
    {
        key: 'format',
        entries: [
            { key: 'layer', cmdId: 'lay.manager' },
            { key: 'textStyle', cmdId: 'draw.textStyle' },
            { key: 'dimStyle', cmdId: 'dim.style' },
            { key: 'mlineStyle', cmdId: 'draw.mlineStyle' },
            { key: 'mleaderStyle', cmdId: 'ann.leaderStyle' },
            { key: 'units', cmdId: 'sys.units' },
            { key: 'matchProp', cmdId: 'vis.matchProp' },
        ],
        dividers: [0, 4],
    },
    {
        key: 'tools',
        entries: [
            { key: 'qselect', cmdId: 'sel.qselect' },
            { key: 'drawOrder', cmdId: 'order.draw' },
            { key: 'dist', cmdId: 'inq.distance' },
            { key: 'area', cmdId: 'inq.area' },
            { key: 'list', cmdId: 'inq.list' },
            { key: 'id', cmdId: 'inq.id' },
            { key: 'script', cmdId: 'sys.script' },
            { key: 'dsettings', cmdId: 'sys.snaps' },
            { key: 'options', cmdId: 'sys.options' },
        ],
        dividers: [1, 5, 6],
    },
    {
        key: 'draw',
        entries: [
            { key: 'line', cmdId: 'draw.line' },
            { key: 'polyline', cmdId: 'draw.polyline' },
            { key: 'circle', cmdId: 'draw.circle' },
            { key: 'arc', cmdId: 'draw.arc' },
            { key: 'rectangle', cmdId: 'draw.rectangle' },
            { key: 'polygon', cmdId: 'draw.polygon' },
            { key: 'ellipse', cmdId: 'draw.ellipse' },
            { key: 'point', cmdId: 'draw.point' },
            { key: 'spline', cmdId: 'draw.spline' },
            { key: 'xline', cmdId: 'draw.xline' },
            { key: 'ray', cmdId: 'draw.ray' },
            { key: 'donut', cmdId: 'draw.donut' },
            { key: 'mline', cmdId: 'draw.mline' },
            { key: 'region', cmdId: 'draw.region' },
            { key: 'boundary', cmdId: 'draw.boundary' },
            { key: 'wipeout', cmdId: 'draw.wipeout' },
            { key: 'revisionCloud', cmdId: 'draw.revisionCloud' },
            { key: 'solid2d', cmdId: 'draw.solid2d' },
            { key: 'gradient', cmdId: 'draw.gradient' },
            { key: 'hatch', cmdId: 'draw.hatch' },
            { key: 'mtext', cmdId: 'draw.mtext' },
            { key: 'text', cmdId: 'draw.text' },
            { key: 'sketch', cmdId: 'draw.sketch' },
            { key: 'hatchEdit', cmdId: 'draw.hatchEdit' },
            { key: 'ddedit', cmdId: 'draw.ddedit' },
        ],
        dividers: [7, 12, 18, 22],
    },
    {
        key: 'dimension',
        entries: [
            { key: 'linear', cmdId: 'dim.linear' },
            { key: 'aligned', cmdId: 'dim.aligned' },
            { key: 'angular', cmdId: 'dim.angular' },
            { key: 'radius', cmdId: 'dim.radius' },
            { key: 'diameter', cmdId: 'dim.diameter' },
            { key: 'ordinate', cmdId: 'dim.ordinate' },
            { key: 'continue', cmdId: 'dim.continue' },
            { key: 'baseline', cmdId: 'dim.baseline' },
            { key: 'arcLength', cmdId: 'dim.arcLength' },
            { key: 'jogged', cmdId: 'dim.jogged' },
            { key: 'space', cmdId: 'dim.space' },
            { key: 'break', cmdId: 'dim.break' },
            { key: 'style', cmdId: 'dim.style' },
            { key: 'centerMark', cmdId: 'dim.centerMark' },
            { key: 'tolerance', cmdId: 'dim.tolerance' },
            { key: 'reassociate', cmdId: 'dim.reassociate' },
            { key: 'override', cmdId: 'dim.override' },
        ],
        dividers: [5, 9, 14],
    },
    {
        key: 'modify',
        entries: [
            { key: 'move', cmdId: 'mod.move' },
            { key: 'copy', cmdId: 'mod.copy' },
            { key: 'rotate', cmdId: 'mod.rotate' },
            { key: 'scale', cmdId: 'mod.scale' },
            { key: 'stretch', cmdId: 'mod.stretch' },
            { key: 'lengthen', cmdId: 'mod.lengthen' },
            { key: 'trim', cmdId: 'mod.trim' },
            { key: 'extend', cmdId: 'mod.extend' },
            { key: 'offset', cmdId: 'mod.offset' },
            { key: 'mirror', cmdId: 'trf.mirror' },
            { key: 'align', cmdId: 'trf.align' },
            { key: 'fillet', cmdId: 'mod.fillet' },
            { key: 'chamfer', cmdId: 'mod.chamfer' },
            { key: 'array', cmdId: 'arr.general' },
            { key: 'arrayRect', cmdId: 'arr.rect' },
            { key: 'arrayPolar', cmdId: 'arr.polar' },
            { key: 'arrayPath', cmdId: 'arr.path' },
            { key: 'erase', cmdId: 'mod.erase' },
            { key: 'explode', cmdId: 'mod.explode' },
            { key: 'join', cmdId: 'mod.join' },
            { key: 'pedit', cmdId: 'mod.pedit' },
            { key: 'splinedit', cmdId: 'mod.splinedit' },
            { key: 'break', cmdId: 'mod.break' },
            { key: 'flatten', cmdId: 'drf.flatten' },
            { key: 'matchProp', cmdId: 'vis.matchProp' },
            { key: 'drawOrder', cmdId: 'order.draw' },
            { key: 'chspace', cmdId: 'space.chspace' },
            { key: 'ncopy', cmdId: 'space.ncopy' },
        ],
        dividers: [4, 8, 12, 16, 17, 23, 25],
    },
    {
        key: 'parametric',
        entries: [
            { key: 'geomConstraints', cmdId: 'par.geom' },
            { key: 'dimConstraints', cmdId: 'par.dim' },
            { key: 'parameters', cmdId: 'par.manager' },
        ],
    },
    {
        key: 'help',
        entries: [
            { key: 'help', cmdId: 'sys.help' },
            { key: 'cui', cmdId: 'sys.cui' },
            { key: 'aliasEdit', cmdId: 'sys.alias' },
            { key: 'appLoad', cmdId: 'sys.appLoad' },
        ],
    },
];

/** Menu titles are plain i18n keys (ribbon.menu.<key>). */
export const MENU_ORDER: string[] = MENU_BAR.map((m) => m.key);
